// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file BreadcrumbItem.tsx
 * @input Uses React use/useRef/useEffect, stylex, theme tokens, BreadcrumbContext,
 *   and (for the menu trigger) usePopover + the shared DropdownMenu item API
 *   (renderDropdownItems, DropdownMenuContext, MENU_ITEM_SELECTOR, useListFocus)
 * @output Exports BreadcrumbItem component and BreadcrumbItemProps
 * @position Individual breadcrumb item; used inside Breadcrumbs
 *
 * A `menu` prop turns the item's link-styled button into a menu trigger whose
 * popover reuses DropdownMenu's item pipeline — the same wiring ContextMenu
 * uses — so menu-item definitions are portable across the menu family.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.doc.mjs
 * - /packages/core/src/Breadcrumbs/Breadcrumbs.test.tsx
 * - /packages/core/src/Breadcrumbs/index.ts
 * - /apps/storybook/stories/Breadcrumbs.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Breadcrumbs/ (showcase blocks)
 */
import React, { use, useCallback, useId, useMemo, useRef, useEffect } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars, radiusVars } from "../theme/tokens.stylex.js";
import { BreadcrumbContext } from "./Breadcrumbs.js";
import { useLinkComponent } from "../Link/useLinkComponent.js";
import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useIcon } from "../Icon/index.js";
import { usePopover } from "../Popover/usePopover.js";
import { useListFocus } from "../hooks/useListFocus.js";
import { useTypeahead } from "../hooks/useTypeahead.js";
import { layerAnimations } from "../Layer/layerAnimations.stylex.js";
import { renderDropdownItems } from "../DropdownMenu/renderDropdownItems.js";
import { DropdownMenuContext } from "../DropdownMenu/DropdownMenuContext.js";
import { MENU_ITEM_ROLES, MENU_ITEM_SELECTOR, MENU_BOUNDARY_SELECTOR } from "../DropdownMenu/menuItemRoles.js";

// =============================================================================
// Props
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const itemStyles = {
  root: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "xzye2dw",
    kogj98: "x1ghz6dp",
    "--separator-display": "xkce8z9 x1ibt0lz",
    $$css: true
  },
  defaultSize: {
    kGuDYH: "xjm74w1",
    kLWn49: "xw6l6zx",
    $$css: true
  },
  supportingSize: {
    kGuDYH: "x141an7d",
    kLWn49: "x1ltkj2j",
    $$css: true
  }
};
const menuStyles = {
  popover: {
    k7Eaqz: "x5w4yej",
    kqGvvJ: "xsq74q5",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * An individual breadcrumb item. Renders as a link (`<a>`) or a span
 * depending on whether it represents the current page.
 *
 * Each item renders its own leading separator, hidden on :first-child via
 * CSS. Auto-current detection uses a post-render effect that checks the
 * DOM — no React child introspection.
 *
 * @example
 * ```
 * <BreadcrumbItem href="/projects">Projects</BreadcrumbItem>
 * <BreadcrumbItem isCurrent>My Project</BreadcrumbItem>
 * ```
 */
export function BreadcrumbItem({
  ref,
  as,
  children,
  href,
  onClick,
  isCurrent: isCurrentProp,
  startIcon,
  menu,
  menuSize,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ...rest
}) {
  const ctx = use(BreadcrumbContext);
  const LinkComponent = useLinkComponent(as);
  const isSupporting = ctx.variant === 'supporting';
  const liRef = useRef(null);
  // Points at the element we render to hold the item's content (the
  // link/button/span in the auto-candidate path). Auto-current detection sets
  // aria-current on this instead of guessing the <li>'s last child.
  const contentRef = useRef(null);
  const isCurrent = isCurrentProp === true;
  const isAutoCandidate = isCurrentProp == null;
  const hasMenu = menu != null;
  const resolvedMenuSize = menuSize ?? (isSupporting ? 'sm' : 'md');

  // `menu` owns the click interaction; a label can't both navigate/act and
  // open a menu on the same activation. Warn once so the (ignored) href/onClick
  // don't silently disappear on the consumer.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && hasMenu) {
      if (href != null) {
        console.warn('BreadcrumbItem: `menu` and `href` are mutually exclusive; `menu` ' + 'takes precedence and `href` is ignored.');
      } else if (onClick != null) {
        console.warn('BreadcrumbItem: `menu` and `onClick` are mutually exclusive; ' + '`menu` takes precedence and `onClick` is ignored.');
      }
    }
  }, [hasMenu, href, onClick]);

  // Auto-detect: if no sibling has aria-current="page" and this is the last
  // non-separator item, set aria-current on our content element.
  // Runs as useEffect (not layout) — only sets an aria attribute, no visual change.
  // Placed on the item's content element (the link/button/span after the
  // separator), matching where the explicit `isCurrent` path sets it, so
  // aria-current lands on the actual interactive element — including when the
  // last item is a link — rather than on the outer <li> (navigation-11).
  useEffect(() => {
    if (!isAutoCandidate) {
      return;
    }
    const li = liRef.current;
    if (!li) {
      return;
    }
    const ol = li.parentElement;
    if (!ol) {
      return;
    }

    // All breadcrumb items (li without aria-hidden are items, with aria-hidden are separators — but we no longer have separator lis)
    const items = Array.from(ol.children);
    const isLast = items.length > 0 && items[items.length - 1] === li;
    const hasExplicit = ol.querySelector('[aria-current="page"]');
    if (isLast && !hasExplicit) {
      // We control the element that holds the content (see the auto-candidate
      // render path below), so set aria-current on that ref rather than
      // assuming a positional last child. Fall back to the <li> only if the
      // ref is somehow unresolved.
      const target = contentRef.current ?? li;
      target.setAttribute('aria-current', 'page');
      return () => {
        target.removeAttribute('aria-current');
      };
    }
  });
  const content = /*#__PURE__*/_jsxs(_Fragment, {
    children: [startIcon && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x78zum5 x6s0dn4 x2lah0s"
      },
      children: startIcon
    }), children]
  });
  if (isCurrent) {
    return /*#__PURE__*/_jsxs("li", {
      ref: mergeRefs(ref, liRef),
      ...mergeProps(themeProps('breadcrumb-item'), stylex.props(itemStyles.root, isSupporting ? itemStyles.supportingSize : itemStyles.defaultSize, xstyle), className, style),
      "data-testid": testId,
      ...rest,
      children: [/*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        ...{
          className: "x11ke7fs x6s0dn4 xv1l7n4 xu0wf1k x87ps6o"
        },
        children: ctx.separator
      }), hasMenu ?
      /*#__PURE__*/
      // A current crumb can also open a sibling menu — the trigger keeps
      // both aria-current="page" and aria-haspopup="menu".
      _jsx(BreadcrumbMenuTrigger, {
        ref: contentRef,
        menu: menu,
        menuSize: resolvedMenuSize,
        isSupporting: isSupporting,
        isCurrent: true,
        label: children,
        children: content
      }) : /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz x1tgivj0"
          },
          1: {
            className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz xv1l7n4"
          }
        }[!!isSupporting << 0],
        "aria-current": "page",
        children: content
      })]
    });
  }

  // Items without isCurrent set render as links (if href) or plain spans.
  // The effect handles adding aria-current for auto-last detection.
  return /*#__PURE__*/_jsxs("li", {
    ref: mergeRefs(ref, liRef),
    ...mergeProps(themeProps('breadcrumb-item'), stylex.props(itemStyles.root, isSupporting ? itemStyles.supportingSize : itemStyles.defaultSize, xstyle), className, style),
    "data-testid": testId,
    ...rest,
    children: [/*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      ...{
        className: "x11ke7fs x6s0dn4 xv1l7n4 xu0wf1k x87ps6o"
      },
      children: ctx.separator
    }), hasMenu ? /*#__PURE__*/_jsx(BreadcrumbMenuTrigger, {
      ref: contentRef,
      menu: menu,
      menuSize: resolvedMenuSize,
      isSupporting: isSupporting,
      label: children,
      children: content
    }) : href != null ? /*#__PURE__*/_jsx(LinkComponent, {
      ref: contentRef,
      href: href,
      onClick: onClick,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk xv1l7n4"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    }) : onClick != null ? /*#__PURE__*/_jsx("button", {
      ref: contentRef,
      type: "button",
      onClick: onClick,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    }) : /*#__PURE__*/_jsx("span", {
      ref: contentRef,
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz x1tgivj0"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw x1pd3egz xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: content
    })]
  });
}
BreadcrumbItem.displayName = 'BreadcrumbItem';

// =============================================================================
// Menu trigger
// =============================================================================

/**
 * The link-styled `<button>` trigger + its `role="menu"` popover. Reuses the
 * DropdownMenu item pipeline (`renderDropdownItems` for arrays, the item
 * components for children) inside a `useListFocus` container that provides
 * `DropdownMenuContext` — the same wiring ContextMenu uses — so menu-item
 * definitions are portable into a breadcrumb with no rewrite.
 */
function BreadcrumbMenuTrigger({
  ref,
  children,
  label,
  menu,
  menuSize,
  isSupporting,
  isCurrent = false
}) {
  const menuId = useId();
  const buttonRef = useRef(null);
  const popover = usePopover({
    onHide: useCallback(() => {
      buttonRef.current?.focus();
    }, []),
    hasLightDismiss: true,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="menu" is the exposed semantics; a modal dialog
    // wrapper would announce an unnamed dialog around the menu.
    role: 'none'
  });
  const chevronDownIcon = useIcon('chevronDown');
  const closeMenu = useCallback(() => {
    popover.hide();
  }, [popover]);
  const {
    listRef,
    handleKeyDown: listNavKeyDown,
    focusFirst,
    focusItem,
    ownsEvent,
    getItems: getMenuItems
  } = useListFocus({
    itemSelector: MENU_ITEM_SELECTOR,
    boundarySelector: MENU_BOUNDARY_SELECTOR,
    wrap: false,
    onEscape: closeMenu
  });
  const typeahead = useTypeahead({
    getItemLabels: () => getMenuItems().map(el => el.textContent),
    onMatch: focusItem,
    getCurrentIndex: () => getMenuItems().findIndex(el => el === document.activeElement || el.contains(document.activeElement))
  });
  const listKeyDown = useCallback(e => {
    // A submenu flyout renders inline inside this menu; its key events bubble
    // up here. Let that level own them — only handle events from this level.
    if (!ownsEvent(e)) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const focused = document.activeElement;
      if (focused && MENU_ITEM_ROLES.has(focused.getAttribute('role') ?? '')) {
        focused.click();
      }
      return;
    }
    // APG menu-button pattern: Tab closes the menu (items are tabIndex={-1}).
    if (e.key === 'Tab') {
      closeMenu();
      return;
    }
    if (typeahead.onKeyDown(e)) {
      e.preventDefault();
      return;
    }
    listNavKeyDown(e);
  }, [listNavKeyDown, closeMenu, typeahead, ownsEvent]);
  const openAndFocus = useCallback(() => {
    popover.show();
    requestAnimationFrame(() => focusFirst());
  }, [popover, focusFirst]);
  const handleClick = useCallback(() => {
    if (popover.isOpen) {
      popover.hide();
    } else {
      openAndFocus();
    }
  }, [popover, openAndFocus]);
  const handleKeyDown = useCallback(e => {
    if (!popover.isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openAndFocus();
      }
    }
  }, [popover.isOpen, openAndFocus]);
  const contextValue = useMemo(() => ({
    closeMenu,
    menuSize
  }), [closeMenu, menuSize]);
  const menuContent = Array.isArray(menu) ? renderDropdownItems(menu) : menu;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("button", {
      ref: mergeRefs(ref, buttonRef, popover.triggerRef),
      type: "button",
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      ...mergeProps(themeProps('breadcrumb-item-menu-trigger'), {
        ...popover.triggerProps,
        'aria-haspopup': 'menu',
        'aria-controls': menuId,
        'aria-current': isCurrent ? 'page' : undefined
      }),
      ...{
        0: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        },
        1: {
          className: "x78zum5 x6s0dn4 xzye2dw xu0wf1k x1hl2dhg x4ohgrr x1ypdohk x1717udv x1ghz6dp xln7xf2 xv1l7n4"
        }
      }[!!isSupporting << 0],
      children: [children, /*#__PURE__*/_jsx("span", {
        "aria-hidden": "true",
        ...{
          className: "x78zum5 x6s0dn4 x2lah0s x141an7d"
        },
        children: chevronDownIcon
      })]
    }), popover.render(/*#__PURE__*/_jsx("div", {
      ref: listRef,
      id: menuId,
      role: "menu",
      "aria-label": typeof label === 'string' ? label : undefined,
      onKeyDown: listKeyDown,
      ...mergeProps(themeProps('breadcrumb-menu'), {
        className: "x9f619 x78zum5 xdt5ytf x1lsbc85 xuyqlj2 x1odjw0f x1fcsqxe xgory14 x9epnlk x1n97fys x87ps6o"
      }),
      children: /*#__PURE__*/_jsx(DropdownMenuContext, {
        value: contextValue,
        children: menuContent
      })
    }), {
      placement: 'below',
      alignment: 'start',
      xstyle: [menuStyles.popover, layerAnimations.below]
    })]
  });
}
BreadcrumbMenuTrigger.displayName = 'BreadcrumbMenuTrigger';